import sharedSupabase from '../config/supabase';

/**
 * AI Item Match Service (web)
 *
 * Thin wrapper around the items-ai-match edge function, which does two jobs:
 *   1. match_items — the AI decides whether new/manual lines (or freshly
 *      created items) ARE existing catalog items, and persists pending
 *      item_cross_reference_suggestions rows itself when item_ids are given.
 *   2. objective_prices — the AI resolves each of an item's purchase options
 *      to a price per objective unit (lb/floz/…) and writes the result into
 *      each option's conversion_snapshot.ai_objective.
 *
 * The edge function may not be deployed in a given environment — every call
 * degrades gracefully (console.warn + []) so the deterministic cross-reference
 * flow and the plain price displays proceed exactly as without it.
 *
 * Every function takes an optional trailing `client` (defaults to the shared
 * supabase singleton) so tests can inject a fake.
 */

const asArray = (value) => (Array.isArray(value) ? value : []);

const warnUnavailable = (what, err) => {
  console.warn(`[ai-item-match] ${what} unavailable (edge function not deployed?):`, err?.message || err);
};

async function invokeAiMatch(body, client) {
  const { data, error } = await client.functions.invoke('items-ai-match', { body });
  if (error) throw new Error(error.message || 'items-ai-match invoke failed');
  return data;
}

/**
 * AI matches for ALREADY-CREATED inventory items. The edge function persists
 * its own pending suggestion rows (channel 'manual') as a side effect; the
 * returned matches are also useful to the caller. Returns [] when the
 * function is unavailable.
 */
export async function fetchAiMatchesForItems(storeId, itemIds, client = sharedSupabase) {
  if (!storeId || !Array.isArray(itemIds) || itemIds.length === 0) return [];
  try {
    const data = await invokeAiMatch(
      { action: 'match_items', store_id: storeId, item_ids: itemIds },
      client,
    );
    return asArray(data?.matches);
  } catch (err) {
    warnUnavailable('AI item matching', err);
    return [];
  }
}

/**
 * AI matches for NOT-YET-CREATED lines (e.g. manual-entry drafts):
 * [{ ref, name, vendor?, brand?, pack?, unit?, price? }]. Each returned match
 * carries line_ref pointing back at the line's ref. Returns [] when the
 * function is unavailable.
 */
export async function fetchAiMatchesForLines(storeId, lines, client = sharedSupabase) {
  if (!storeId || !Array.isArray(lines) || lines.length === 0) return [];
  try {
    const data = await invokeAiMatch(
      { action: 'match_items', store_id: storeId, lines },
      client,
    );
    return asArray(data?.matches);
  } catch (err) {
    warnUnavailable('AI item matching', err);
    return [];
  }
}

/**
 * Re-resolve one item's relationship price comparison on the server: the
 * deterministic pass runs first, ONE AI call only when a package stays
 * uninterpretable; results persist to conversion_snapshot.price_comparison +
 * the item's comparison_* columns. Returns { comparison, resolutions } —
 * comparison is the whole relationship resolution, resolutions a per-option
 * array kept for older callers. Returns null when unavailable.
 */
export async function fetchRelationshipComparison(storeId, inventoryItemId, client = sharedSupabase) {
  if (!storeId || !inventoryItemId) return null;
  try {
    const data = await invokeAiMatch(
      { action: 'objective_prices', store_id: storeId, inventory_item_id: inventoryItemId },
      client,
    );
    return {
      comparison: data?.comparison ?? null,
      resolutions: asArray(data?.resolutions),
    };
  } catch (err) {
    warnUnavailable('relationship price comparison', err);
    return null;
  }
}


/**
 * Shape an AI match row into the candidate shape LinkItemDialog already
 * renders (see utils/itemCrossReference findCrossReferenceCandidates), tagged
 * aiMatch so the dialog labels it as AI-suggested.
 */
export function aiMatchToCandidate(match) {
  return {
    itemId: match.inventory_item_id,
    itemDescription: match.item_description || '',
    score: match.confidence,
    reason: match.reason || '',
    decision: 'suggest',
    aiMatch: true,
    aiRejected: asArray(match.rejected),
  };
}

/**
 * Merge AI matches into a deterministic candidate list for the LinkItemDialog
 * queue, deduplicated by candidate item id. The AI is the decision-maker: its
 * entry REPLACES a deterministic candidate for the same item (keeping the
 * deterministic slot's position), and AI-only matches append after. Capped at
 * 3 — the dialog's documented maximum.
 */
export function mergeAiCandidates(deterministic = [], aiMatches = []) {
  const byId = new Map();
  asArray(deterministic).forEach((candidate) => {
    if (candidate && candidate.itemId !== null && candidate.itemId !== undefined) {
      byId.set(String(candidate.itemId), candidate);
    }
  });
  asArray(aiMatches).forEach((match) => {
    if (!match || match.inventory_item_id === null || match.inventory_item_id === undefined) return;
    byId.set(String(match.inventory_item_id), aiMatchToCandidate(match));
  });
  return [...byId.values()].slice(0, 3);
}
